import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, MenuItem, Select, FormControl,
  InputLabel, IconButton, Autocomplete, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { AuthContext } from '../context/AuthProvider';
import { Spinner, EquipmentImage } from '../components';
import ProposalViewJobOverview from '../components/ProposalView/proposalView.jobOverview';
import ProposalViewAirflow from '../components/ProposalView/proposalView.airflow';
import ProposalViewInvestment from '../components/ProposalView/proposalView.investment';
import ProposalViewGuarantees from '../components/ProposalView/proposalView.guarantees';

import propertiesServices from '../services/propertiesServices';
import proposalsServices from '../services/proposalsServices';
import equipmentTypesServices from '../services/equipmentTypesServices';
import { calculateTotalPay, calculateTotalRebates, calculateTotalInstants } from '../utils/proposalCalculations';

const fontFamily = '"Inter", sans-serif';
const CARD_LABELS = ['GOOD', 'BETTER', 'BEST'];
const CARD_COLORS = ['#2e7d32', '#1565c0', '#6a1b9a'];
const SEAL_IMAGE = '/assets/airmaxx-recommendation-seal.png?v=5';

const Presentation = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyProposals, setPropertyProposals] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [cards, setCards] = useState([
    { label: CARD_LABELS[0], proposalId: '', proposal: null, expanded: false, subtitle: '' },
    { label: CARD_LABELS[1], proposalId: '', proposal: null, expanded: false, subtitle: '' },
  ]);
  const [recommendedIndex, setRecommendedIndex] = useState(null);

  useEffect(() => {
    loadEquipmentTypes();
  }, []);

  const loadEquipmentTypes = async () => {
    try {
      const response = await equipmentTypesServices.getEquipmentTypes();
      if (response.status === 200) {
        const typesMap = {};
        response.data.forEach(type => { typesMap[type.name] = type.imageUrl || null; });
        setEquipmentTypes(typesMap);
      }
    } catch (e) { /* silent */ }
  };

  const searchTimerRef = useRef(null);

  const handleSearch = useCallback((query) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query || query.trim().length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await propertiesServices.searchRequest(0, 30, query.trim(), false);
        if (response.status === 200 || response.status === 206) {
          setSearchResults(response.data?.data || []);
        }
      } catch (e) { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
  }, []);

  const handleSelectProperty = async (property) => {
    if (!property) return;
    setSelectedProperty(property);
    try {
      const response = await propertiesServices.getPropertyById(property.id);
      if (response.status === 200) {
        const fullProp = response.data;
        const proposals = (fullProp.proposals || []).filter(
          p => !p.deletedAt && ['Active', 'Cold', 'Pending'].includes(p.status)
        );
        setPropertyProposals(proposals);
      }
    } catch (e) { setPropertyProposals([]); }
  };

  const getAddress = () => {
    if (!selectedProperty) return '';
    const p = selectedProperty;
    if (p.place?.label) return p.place.label;
    const parts = [p.streetNumber, p.streetName, p.city, p.state, p.zipCode].filter(Boolean);
    return parts.join(' ') || p.sourceAddress || p.address || '';
  };

  const handleSelectProposal = async (cardIndex, proposalId) => {
    setCards(prev => prev.map((c, i) =>
      i === cardIndex ? { ...c, proposalId, proposal: null } : c
    ));

    if (recommendedIndex === cardIndex) setRecommendedIndex(null);

    if (!proposalId) return;
    try {
      const response = await proposalsServices.getProposalById(proposalId);
      if (response.status === 200) {
        setCards(prev => prev.map((c, i) =>
          i === cardIndex ? { ...c, proposalId, proposal: response.data } : c
        ));
      }
    } catch (e) { /* silent */ }
  };

  const handleToggleExpand = (cardIndex) => {
    setCards(prev => prev.map((c, i) =>
      i === cardIndex ? { ...c, expanded: !c.expanded } : c
    ));
  };

  const handleAddBest = () => {
    if (cards.length >= 3) return;
    setCards([...cards, { label: CARD_LABELS[2], proposalId: '', proposal: null, expanded: false, subtitle: '' }]);
  };

  const handleReset = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedProperty(null);
    setPropertyProposals([]);
    setCards([
      { label: CARD_LABELS[0], proposalId: '', proposal: null, expanded: false, subtitle: '' },
      { label: CARD_LABELS[1], proposalId: '', proposal: null, expanded: false, subtitle: '' },
    ]);
    setRecommendedIndex(null);
  };

  const handleSubtitleChange = (cardIndex, subtitle) => {
    setCards(prev => prev.map((c, i) =>
      i === cardIndex ? { ...c, subtitle } : c
    ));
  };

  const handleToggleRecommend = (cardIndex) => {
    setRecommendedIndex(prev => prev === cardIndex ? null : cardIndex);
  };

  const expandedCards = cards.filter(c => c.expanded && c.proposal);

  if (!userData) return <Spinner />;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Search bar + RESET */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Autocomplete
          freeSolo
          fullWidth
          options={searchResults}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            const p = option;
            if (p.place?.label) return p.place.label;
            return [p.streetNumber, p.streetName, p.city, p.state, p.zipCode].filter(Boolean).join(' ') || p.sourceAddress || '';
          }}
          inputValue={searchQuery}
          onInputChange={(_, value) => {
            setSearchQuery(value);
            handleSearch(value);
          }}
          onChange={(_, value) => {
            if (value && typeof value !== 'string') handleSelectProperty(value);
          }}
          loading={searchLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search address..."
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const addr = option.place?.label
              || [option.streetNumber, option.streetName, option.city, option.state, option.zipCode].filter(Boolean).join(' ')
              || option.sourceAddress || '';
            return <li {...props} key={option.id}>{addr}</li>;
          }}
        />
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          sx={{ whiteSpace: 'nowrap', borderColor: '#0d3c61', color: '#0d3c61', fontWeight: 600 }}
        >
          RESET
        </Button>
      </Paper>

      {/* Address Banner */}
      {selectedProperty && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)' }}>
          <Typography sx={{ fontFamily, fontSize: 18, color: '#333', lineHeight: 1.6 }}>
            What can <strong style={{ color: '#0d3c61' }}>AIRMAXX</strong> do to improve the indoor climate in your home at{' '}
            <strong style={{ color: '#0d3c61' }}>{getAddress()}</strong>?
          </Typography>
        </Paper>
      )}

      {/* Cards Row */}
      {selectedProperty && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
          {cards.map((card, idx) => (
            <PresentationCard
              key={idx}
              card={card}
              cardIndex={idx}
              color={CARD_COLORS[idx]}
              proposals={propertyProposals}
              usedProposalIds={cards.map(c => c.proposalId).filter(Boolean)}
              onSelectProposal={handleSelectProposal}
              onToggleExpand={handleToggleExpand}
              onMyChoice={() => card.proposalId && navigate(`/proposals/view/${card.proposalId}`)}
              isExpanded={card.expanded}
              isRecommended={recommendedIndex === idx}
              onToggleRecommend={handleToggleRecommend}
              onSubtitleChange={handleSubtitleChange}
            />
          ))}

          {/* + Button */}
          {cards.length < 3 && (
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 8 }}>
              <IconButton
                onClick={handleAddBest}
                sx={{
                  width: 48, height: 48,
                  backgroundColor: '#0d3c61', color: '#fff',
                  '&:hover': { backgroundColor: '#1a5a8a' },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* Expanded Comparison Area */}
      {expandedCards.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex' }}>
            {expandedCards.map((card, idx) => {
              const colorIdx = CARD_LABELS.indexOf(card.label);
              return (
                <Box
                  key={card.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderTop: `3px solid ${CARD_COLORS[colorIdx]}`,
                    borderLeft: idx > 0 ? '1px solid #d0d0d0' : 'none',
                    pt: 1,
                    px: 1,
                  }}
                >
                  <Typography sx={{
                    fontFamily, fontWeight: 700, fontSize: 16, textAlign: 'center',
                    mb: card.subtitle ? 0.5 : 2,
                    color: CARD_COLORS[colorIdx],
                  }}>
                    {card.label} — {card.proposal?.publicId}
                  </Typography>
                  {card.subtitle && (
                    <Typography sx={{
                      fontFamily, fontWeight: 600, fontSize: 12, textAlign: 'center',
                      color: '#555', mb: 2,
                    }}>
                      {card.subtitle}
                    </Typography>
                  )}
                  <ProposalSections proposal={card.proposal} equipmentTypes={equipmentTypes} />
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

/* ─── Card Component ─── */

const PresentationCard = ({
  card, cardIndex, color, proposals, usedProposalIds,
  onSelectProposal, onToggleExpand, onMyChoice, isExpanded,
  isRecommended, onToggleRecommend, onSubtitleChange,
}) => {
  const [editingSubtitle, setEditingSubtitle] = useState(false);
  const [subtitleDraft, setSubtitleDraft] = useState('');
  const subtitleInputRef = useRef(null);

  const totalPay = card.proposal ? calculateTotalPay(card.proposal) : 0;

  const availableProposals = proposals.filter(
    p => p.id === card.proposalId || !usedProposalIds.includes(p.id)
  );

  const handleStartEditSubtitle = () => {
    setSubtitleDraft(card.subtitle || '');
    setEditingSubtitle(true);
    setTimeout(() => subtitleInputRef.current?.focus(), 50);
  };

  const handleFinishEditSubtitle = () => {
    setEditingSubtitle(false);
    onSubtitleChange(cardIndex, subtitleDraft.trim());
  };

  return (
    <Paper
      sx={{
        flex: 1, p: 2, minWidth: 220, position: 'relative', overflow: 'visible',
        border: isExpanded ? `2px solid ${color}` : '1px solid #e0e0e0',
        transition: 'border 0.2s',
      }}
    >
      {/* Recommendation Seal */}
      {isRecommended && (
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: -16,
            width: 120,
            height: 52,
            zIndex: 10,
            pointerEvents: 'none',
            animation: 'sealStamp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes sealStamp': {
              '0%': { transform: 'scale(0)', opacity: 0 },
              '60%': { transform: 'scale(1.1)', opacity: 1 },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        >
          <img
            src={SEAL_IMAGE}
            alt="Recommended"
            style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.25))' }}
          />
        </Box>
      )}

      {/* Add subtitle button */}
      {!card.subtitle && !editingSubtitle && (
        <IconButton
          size="small"
          onClick={handleStartEditSubtitle}
          sx={{
            position: 'absolute', top: 6, right: 6,
            width: 24, height: 24,
            backgroundColor: '#4caf50', color: '#fff',
            '&:hover': { backgroundColor: '#388e3c' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      {/* Label */}
      <Typography
        sx={{
          fontFamily, fontWeight: 800, fontSize: 22, textAlign: 'center', mb: 0.5,
          color, cursor: 'pointer', userSelect: 'none',
          '&:hover': { opacity: 0.8 },
        }}
        onClick={() => card.proposal && onToggleExpand(cardIndex)}
      >
        {card.label}
      </Typography>

      {/* Subtitle area */}
      {editingSubtitle ? (
        <TextField
          inputRef={subtitleInputRef}
          value={subtitleDraft}
          onChange={(e) => setSubtitleDraft(e.target.value)}
          onBlur={handleFinishEditSubtitle}
          onKeyDown={(e) => { if (e.key === 'Enter') handleFinishEditSubtitle(); }}
          size="small"
          fullWidth
          placeholder="e.g. HEAT PUMP and AIR HANDLER"
          sx={{ mb: 0.5 }}
          inputProps={{ style: { textAlign: 'center', fontFamily, fontWeight: 600, fontSize: 12 } }}
        />
      ) : card.subtitle ? (
        <Typography
          onClick={handleStartEditSubtitle}
          sx={{
            fontFamily, fontWeight: 600, fontSize: 12, textAlign: 'center',
            color: '#555', cursor: 'pointer', mb: 0.5,
            '&:hover': { color: '#333', textDecoration: 'underline' },
          }}
        >
          {card.subtitle}
        </Typography>
      ) : (
        <Box sx={{ mb: 0.5 }} />
      )}

      {/* Price */}
      {card.proposal && (
        <Typography sx={{
          fontFamily, fontWeight: 700, fontSize: 20, textAlign: 'center', mb: 2, color: '#333',
        }}>
          ${totalPay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      )}

      {/* PO Dropdown */}
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Select PO</InputLabel>
        <Select
          value={card.proposalId}
          label="Select PO"
          onChange={(e) => onSelectProposal(cardIndex, e.target.value)}
        >
          <MenuItem value=""><em>— Select —</em></MenuItem>
          {availableProposals.map(p => (
            <MenuItem key={p.id} value={p.id}>{p.publicId}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Recommend / Expand / My Choice */}
      {card.proposal && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            size="small"
            variant={isRecommended ? 'contained' : 'outlined'}
            fullWidth
            onClick={() => onToggleRecommend(cardIndex)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#8b1a1a',
              color: isRecommended ? '#fff' : '#8b1a1a',
              backgroundColor: isRecommended ? '#8b1a1a' : 'transparent',
              '&:hover': {
                backgroundColor: isRecommended ? '#6d1515' : 'rgba(139, 26, 26, 0.08)',
                borderColor: '#8b1a1a',
              },
            }}
          >
            {isRecommended ? 'Recommended' : 'Recommend'}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => onToggleExpand(cardIndex)}
              sx={{ textTransform: 'none', borderColor: color, color }}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={onMyChoice}
              sx={{ textTransform: 'none', backgroundColor: color, '&:hover': { opacity: 0.9, backgroundColor: color } }}
            >
              MY CHOICE
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

/* ─── Proposal Sections Renderer ─── */

const ProposalSections = ({ proposal, equipmentTypes }) => {
  if (!proposal) return null;

  const totalPay = calculateTotalPay(proposal);
  const totalRebates = calculateTotalRebates(proposal);
  const totalInstants = calculateTotalInstants(proposal);

  const getJobOverview = () => {
    if (proposal.jobOverview) return proposal.jobOverview;
    if (proposal.proposalSystems) {
      const parts = [];
      proposal.proposalSystems.forEach(s => { if (s.notes) parts.push(s.notes); });
      if (parts.length > 0) return parts.join('\n\n');
    }
    return null;
  };

  const systemsWithEquipment = (proposal.proposalSystems || []).filter(
    s => s.equipments && s.equipments.length > 0 && !s.deletedAt
  );
  const showSystemNames = systemsWithEquipment.length > 1;

  let allScopeOfWorks = [];
  if (proposal.scopeOfWorks && proposal.scopeOfWorks.length > 0) {
    allScopeOfWorks = proposal.scopeOfWorks.map(w => ({ ...w }));
  } else if (proposal.proposalSystems) {
    proposal.proposalSystems.forEach(system => {
      if (system.scopeOfWorks && system.scopeOfWorks.length > 0) {
        system.scopeOfWorks.forEach(w => allScopeOfWorks.push({ ...w, systemName: system.name }));
      }
    });
  }

  let allWarranties = [];
  if (proposal.warranties && proposal.warranties.length > 0) {
    allWarranties = proposal.warranties.map(w => ({ ...w }));
  } else if (proposal.proposalSystems) {
    proposal.proposalSystems.forEach(system => {
      if (system.warranties && system.warranties.length > 0) {
        system.warranties.forEach(w => allWarranties.push({ ...w, systemName: system.name }));
      }
    });
  }

  return (
    <Box>
      {/* Project Overview */}
      {proposal.proposalType !== 'Service' && (
        <ProposalViewJobOverview description={getJobOverview()} />
      )}

      {/* Equipment */}
      {proposal.proposalType !== 'Service' && systemsWithEquipment.length > 0 && (
        <Box sx={{ mb: 4, px: 2 }}>
          <SectionHeader title="Equipment" />
          {systemsWithEquipment.map((system, sysIdx) => (
            <Box key={system.id || sysIdx} sx={{ mb: sysIdx < systemsWithEquipment.length - 1 ? 3 : 0 }}>
              {showSystemNames && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e0e0', pb: 0.5, display: 'inline-block' }}>
                    {system.name}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                {system.equipments.map((eq, eqIdx) => {
                  const imgUrl = eq.imageUrl || equipmentTypes[eq.equipmentType] || null;
                  return (
                    <Box key={eq.id || eqIdx} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column' }}>
                      {imgUrl && (
                        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
                          <EquipmentImage src={imgUrl} alt={eq.equipmentName || eq.modelNumber} />
                        </Box>
                      )}
                      <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 500, color: '#1a1a1a', mb: 0.5 }}>
                        {eq.equipmentName || eq.modelNumber}
                      </Typography>
                      {eq.description && (
                        <Typography sx={{ fontFamily, fontSize: 11, color: '#666', lineHeight: 1.5 }}>
                          {eq.description}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Airflow */}
      {proposal.proposalType !== 'Service' && proposal.proposalAirflows && proposal.proposalAirflows.length > 0 && (
        <ProposalViewAirflow airflows={proposal.proposalAirflows} />
      )}

      {/* Scope of Work */}
      {allScopeOfWorks.length > 0 && (
        <Box sx={{ mb: 4, px: 2 }}>
          <SectionHeader title="Scope of Work" />
          <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 2 }}>
            {allScopeOfWorks.map((work, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: idx < allScopeOfWorks.length - 1 ? 1.5 : 0 }}>
                <Typography sx={{ color: '#4caf50', mr: 1, fontWeight: 700, fontSize: 14 }}>✓</Typography>
                <Typography sx={{ fontFamily, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
                  {work.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Warranties */}
      {allWarranties.length > 0 && (
        <Box sx={{ mb: 4, px: 2 }}>
          <SectionHeader title="Warranties" />
          <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, overflow: 'hidden' }}>
            {allWarranties.map((warranty, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderBottom: idx < allWarranties.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                <Typography sx={{ fontFamily, fontSize: 13, color: '#333' }}>{warranty.description}</Typography>
                <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61' }}>
                  {isNaN(warranty.years) ? warranty.years : `${warranty.years} Years`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Discounts, Rebates, Total */}
      <ProposalViewInvestment
        lineItems={[]}
        totalPay={totalPay}
        rebates={totalRebates}
        instants={totalInstants}
        discountsList={proposal?.discounts || []}
        rebatesList={proposal?.contractRebates || []}
        financingOptions={proposal.paymentNotes}
        isSigned={!!(proposal?.signatureId || proposal?.signature)}
      />

      {/* Guarantees */}
      <ProposalViewGuarantees guarantees={proposal?.guarantees} />
    </Box>
  );
};

/* ─── Shared Section Header ─── */

const SectionHeader = ({ title }) => (
  <Box sx={{ display: 'inline-block', mb: 1 }}>
    <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.75 }}>
      <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {title}
      </Typography>
    </Box>
  </Box>
);

export default Presentation;
